"use client"

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"

import {
  Song,
  Playlist,
  CurrentTrackData,
  getPlaylistData,
} from "./musicContext"

import {
  useListeningInsights,
} from "@/app/pages/insights/hooks/useListeningInsights"

/* =========================================================
   CONSTANTS
========================================================= */

const STORAGE_KEY =
  "music-player-state"

/* =========================================================
   TYPES
========================================================= */

type PreloadedSong = {
  song: Song
  index: number
  playlist: Playlist
  src: string
}

/* =========================================================
   PROPS
========================================================= */

type UseMusicPlayerProps = {
  playlist: Playlist[]

  currentSong: Song | null
  setCurrentSong: React.Dispatch<
    React.SetStateAction<Song | null>
  >

  currentPlaylist: Playlist | null
  setCurrentPlaylist: React.Dispatch<
    React.SetStateAction<Playlist | null>
  >

  currentSongIndex: number
  setCurrentSongIndex: React.Dispatch<
    React.SetStateAction<number>
  >

  currentTrackData:
    | CurrentTrackData
    | null

  currentTrackDataRef:
    React.MutableRefObject<
      CurrentTrackData | null
    >

  updateCurrentTrackData: (
    data: CurrentTrackData | null,
  ) => void

  isShuffle: boolean
}

/* =========================================================
   RETURN TYPE
========================================================= */
export type UseMusicPlayerReturn = {
  audioRef:
    React.MutableRefObject<HTMLAudioElement | null>

  nextAudioRef:
    React.MutableRefObject<HTMLAudioElement | null>

  isPlaying: boolean

  setIsPlaying:
    React.Dispatch<
      React.SetStateAction<boolean>
    >

  isLoading: boolean

  isMusicLoading: boolean

  setIsMusicLoading:
    React.Dispatch<
      React.SetStateAction<boolean>
    >

  isBuffering: boolean

  setIsBuffering:
    React.Dispatch<
      React.SetStateAction<boolean>
    >

  playSong: (
    song: Song,
    index?: number,
    playlist?: Playlist,
  ) => void

  resumeAudio: () => Promise<boolean>

  seek: (time: number) => void

  insightsPlayingRef:
    React.MutableRefObject<boolean>

  insightsTrackRef:
    React.MutableRefObject<string | null>

  radioPlayRequestId:
    React.MutableRefObject<number>

  radioHasStartedRef:
    React.MutableRefObject<boolean>

  wantsToPlayRef:
    React.MutableRefObject<boolean>

  startTrack: ReturnType<
    typeof useListeningInsights
  >["startTrack"]

  pause: ReturnType<
    typeof useListeningInsights
  >["pause"]

  resume: ReturnType<
    typeof useListeningInsights
  >["resume"]

  stop: ReturnType<
    typeof useListeningInsights
  >["stop"]
}

/* =========================================================
   HOOK
========================================================= */

export const useMusicPlayer = ({
  playlist,

  currentSong,
  setCurrentSong,

  currentPlaylist,
  setCurrentPlaylist,

  currentSongIndex,
  setCurrentSongIndex,

  currentTrackData,
  currentTrackDataRef,

  updateCurrentTrackData,

  isShuffle,
}: UseMusicPlayerProps): UseMusicPlayerReturn => {
  /* =======================================================
     INSIGHTS
  ======================================================= */

  const {
    startTrack,
    pause,
    resume,
    stop,
  } = useListeningInsights()

  const insightsTrackRef =
    useRef<string | null>(null)

  const insightsPlayingRef =
    useRef(false)

  /* =======================================================
     LOADING
  ======================================================= */

  const [isBuffering, setIsBuffering] =
    useState(false)

  const [isMusicLoading, setIsMusicLoading] =
    useState(false)

  const isLoading =
    isMusicLoading ||
    isBuffering

  /* =======================================================
     AUDIO
  ======================================================= */

  const audioRef =
    useRef<HTMLAudioElement | null>(null)

  const nextAudioRef =
    useRef<HTMLAudioElement | null>(null)

  /* =======================================================
     REQUEST IDS
  ======================================================= */

  const playRequestId =
    useRef(0)

  const radioPlayRequestId =
    useRef(0)

  const preloadRequestId =
    useRef(0)

  /* =======================================================
     INTENTION
  ======================================================= */

  const wantsToPlayRef =
    useRef(false)

  /* =======================================================
     STARTED FLAGS
  ======================================================= */

  const musicHasStartedRef =
    useRef(false)

  const radioHasStartedRef =
    useRef(false)

  const audioGenerationRef =
  useRef(0)

  /* =======================================================
     PRELOAD STATE
  ======================================================= */

  const preloadedSongRef =
    useRef<PreloadedSong | null>(null)

  /* =======================================================
     PLAYING
  ======================================================= */

  const [isPlaying, setIsPlaying] =
    useState(false)

  /* =======================================================
     INITIALIZE AUDIO ELEMENTS
  ======================================================= */

  useEffect(() => {
    if (
      typeof window === "undefined"
    ) {
      return
    }

    const audio =
      new Audio()

    const nextAudio =
      new Audio()

    audio.preload = "auto"
    nextAudio.preload = "auto"

    audio.setAttribute(
      "playsinline",
      "",
    )

    nextAudio.setAttribute(
      "playsinline",
      "",
    )

    audio.setAttribute(
      "webkit-playsinline",
      "",
    )

    nextAudio.setAttribute(
      "webkit-playsinline",
      "",
    )

    audioRef.current =
      audio

    nextAudioRef.current =
      nextAudio

    return () => {
      audio.pause()
      nextAudio.pause()

      audio.removeAttribute(
        "src",
      )

      nextAudio.removeAttribute(
        "src",
      )

      audio.load()
      nextAudio.load()

      audioRef.current =
        null

      nextAudioRef.current =
        null
    }
  }, [])

  /* =======================================================
     UPDATE CURRENT TRACK DATA
  ======================================================= */

  const updateTrackData =
    useCallback(
      (
        data: CurrentTrackData | null,
      ) => {
        updateCurrentTrackData(
          data,
        )
      },
      [
        updateCurrentTrackData,
      ],
    )

  /* =======================================================
     INSIGHTS TRACK ID
  ======================================================= */
  const getInsightsTrackId =
    useCallback(
      (
        song: Song,
        playlist?: Playlist,
        index = 0,
        radioId?: string,
      ) => {
        return [
          radioId ??
            "music",

          playlist?.id ??
            playlist?.title ??
            "unknown",

          index,

          song.songName ??
            "",

          song.songArtist ??
            "",
        ].join("|")
      },
      [],
    )

  /* =======================================================
     START INSIGHTS TRACKING
  ======================================================= */
  const startInsightsTracking =
    useCallback(
      (
        song: Song,
        playlist?: Playlist,
        index = 0,
        radioId?: string,
        radioName?: string,
        radioColor?: string,
        radioLogo?: string,
      ) => {
        const trackId =
          getInsightsTrackId(
            song,
            playlist,
            index,
            radioId,
          )

        if (
          insightsTrackRef.current ===
          trackId
        ) {
          return
        }

        insightsTrackRef.current =
          trackId

        startTrack({
          id: trackId,

          title:
            song.songName ||
            "Unknown track",

          artist:
            song.songArtist ||
            "Unknown artist",

          image:
            song.songimg,

          duration: 0,

          radioId,

          radioName,

          radioColor,

          radioLogo,
        })
      },
      [
        getInsightsTrackId,
        startTrack,
      ],
    )

  /* =======================================================
     ATTACH MUSIC AUDIO EVENTS
  ======================================================= */
  const attachMusicAudioEvents =
    useCallback(
      (
        audio: HTMLAudioElement,
        song: Song,
        songPlaylist: Playlist,
        index: number,
        src: string,
        requestId: number,
      ) => {
        /* =================================================
           CLEAN PREVIOUS EVENTS
        ================================================= */
        audio.onloadedmetadata =
          null

        audio.oncanplay =
          null

        audio.onplaying =
          null

        audio.onerror =
          null

        audio.onended =
          null

        audio.onwaiting =
          null

        audio.onstalled =
          null

        audio.onabort =
          null

        /* =================================================
           PLAYING
        ================================================= */
        audio.onplaying =
          () => {
            if (
              requestId !==
              playRequestId.current
            ) {
              return
            }

            if (
              !wantsToPlayRef.current
            ) {
              audio.pause()

              musicHasStartedRef.current =
                false

              setIsMusicLoading(
                false,
              )

              setIsPlaying(
                false,
              )

              return
            }

            const wasTracking =
              insightsPlayingRef.current

            musicHasStartedRef.current =
              true

            insightsPlayingRef.current =
              true

            setIsMusicLoading(
              false,
            )

            setIsPlaying(
              true,
            )

            /* setMediaPlaybackState(
              "playing",
            ) */

            if (
              !insightsTrackRef.current
            ) {
              startInsightsTracking(
                song,
                songPlaylist,
                index,
              )
            }

            if (
              wasTracking
            ) {
              return
            }

            resume()
          }

        /* =================================================
           WAITING
        ================================================= */

        audio.onwaiting =
          () => {
            if (
              requestId !==
              playRequestId.current
            ) {
              return
            }

            if (
              musicHasStartedRef.current
            ) {
              return
            }

            if (
              wantsToPlayRef.current
            ) {
              setIsMusicLoading(
                true,
              )
            }
          }

        /* =================================================
           STALLED
        ================================================= */

        audio.onstalled =
          () => {
            if (
              requestId !==
              playRequestId.current
            ) {
              return
            }

            if (
              musicHasStartedRef.current
            ) {
              return
            }

            if (
              wantsToPlayRef.current
            ) {
              setIsMusicLoading(
                true,
              )
            }
          }

        /* =================================================
           ABORT
        ================================================= */

        audio.onabort =
          () => {
            if (
              requestId !==
              playRequestId.current
            ) {
              return
            }

            if (
              !wantsToPlayRef.current
            ) {
              musicHasStartedRef.current =
                false

              setIsMusicLoading(
                false,
              )

              setIsPlaying(
                false,
              )

              return
            }

            musicHasStartedRef.current =
              false

            setIsMusicLoading(
              false,
            )

            setIsPlaying(
              false,
            )
          }

        /* =================================================
           ERROR
        ================================================= */

        audio.onerror =
          () => {
            if (
              requestId !==
              playRequestId.current
            ) {
              return
            }

            musicHasStartedRef.current =
              false

            wantsToPlayRef.current =
              false

            setIsMusicLoading(
              false,
            )

            setIsPlaying(
              false,
            )

            /* setMediaPlaybackState(
              "none",
            ) */

            console.error(
              "[MusicPlayer] Error cargando:",
              src,
            )
          }
      },
      [
        resume,
        startInsightsTracking, /* setMediaPlaybackState, */
      ],
    )

  /* =======================================================
     PLAY PRELOADED NEXT
  ======================================================= */

  const playPreloadedNext =
    useCallback(
      async () => {
        const currentAudio =
          audioRef.current

        const nextAudio =
          nextAudioRef.current

        const preloaded =
          preloadedSongRef.current

        if (
          !currentAudio ||
          !nextAudio ||
          !preloaded
        ) {
          return false
        }

        wantsToPlayRef.current =
          true

        if (
          preloaded.playlist !==
          currentPlaylist
        ) {
          return false
        }

        if (
          nextAudio.readyState <
          HTMLMediaElement.HAVE_CURRENT_DATA
        ) {
          return false
        }

        const nextSong =
          preloaded.song

        const nextIndex =
          preloaded.index

        const songPlaylist =
          preloaded.playlist

        const src =
          preloaded.src

        /* ===============================================
           SWAP AUDIO ELEMENTS
        =============================================== */
        /* audioRef.current =
          nextAudio

        nextAudioRef.current =
          currentAudio

        const requestId =
          ++playRequestId.current */


        /* ===============================================
           SWAP AUDIO ELEMENTS
        =============================================== */

        const requestId =
          ++playRequestId.current

        /*
         * El elemento que estaba preparado pasa a ser
         * el audio principal.
         *
         * IMPORTANTE:
         * No intercambiamos las referencias aquí.
         * El audio principal debe seguir siendo
         * audioRef.current para que radio y música
         * compartan siempre el mismo elemento.
         */

        currentAudio.pause()

        currentAudio.removeAttribute("src")
        currentAudio.load()

        /* audioRef.current =
          nextAudio

        nextAudioRef.current =
          currentAudio */


        /* ===============================================
           CLEAN OLD AUDIO
        =============================================== */

        currentAudio.pause()

        currentAudio.onloadedmetadata =
          null

        currentAudio.oncanplay =
          null

        currentAudio.onplaying =
          null

        currentAudio.onerror =
          null

        currentAudio.onended =
          null

        currentAudio.onwaiting =
          null

        currentAudio.onstalled =
          null

        currentAudio.onabort =
          null

        /* ===============================================
           STATE
        =============================================== */

        setCurrentSong(
          nextSong,
        )

        setCurrentPlaylist(
          songPlaylist,
        )

        setCurrentSongIndex(
          nextIndex,
        )

        setIsMusicLoading(
          true,
        )

        setIsPlaying(
          false,
        )

        musicHasStartedRef.current =
          false

        /* ===============================================
           TRACK DATA
        =============================================== */

        updateTrackData({
          type: "song",

          track: src,

          trackName:
            nextSong.songName,

          trackArtist:
            nextSong.songArtist,

          trackImg:
            nextSong.songimg,

          playlistLink:
            songPlaylist.link,

          playlistTitle:
            songPlaylist.title,

          trackKey: [
            songPlaylist.id,
            nextIndex,
          ],

          trackTimeElapsed: 0,

          _ts: Date.now(),
        })

        /* ===============================================
           MEDIA SESSION
        =============================================== */
        /* updateMediaSession(
          nextSong,
        )

        claimMediaSession() */

        /* ===============================================
           PRELOAD CONSUMED
        =============================================== */

        preloadedSongRef.current =
          null

        /* ===============================================
           EVENTS
        =============================================== */

        attachMusicAudioEvents(
          nextAudio,
          nextSong,
          songPlaylist,
          nextIndex,
          src,
          requestId,
        )

        try {
          await nextAudio.play()

          if (
            requestId !==
            playRequestId.current
          ) {
            return false
          }

          /*
           * El estado real de playing
           * se confirma mediante "playing".
           */

          return true
        } catch (error: any) {
          console.warn(
            "[Music] Preloaded next track play failed:",
            error,
          )

          return false
        }
      },
      [
        currentPlaylist,
        setCurrentPlaylist,
        setCurrentSong,
        setCurrentSongIndex,
        updateTrackData,
        attachMusicAudioEvents,
      ],
    )

  /* =======================================================
     PLAY SONG
  ======================================================= */

  const playSong =
    useCallback(
      (
        song: Song,
        index = 0,
        songPlaylist?: Playlist,
      ) => {
        const audio =
          audioRef.current

        if (
          !audio ||
          !song
        ) {
          return
        }

        /* ===============================================
           USER INTENTION
        =============================================== */

        wantsToPlayRef.current =
          true

        /* ===============================================
           INVALIDATE PRELOAD
        =============================================== */

        preloadRequestId.current++

        preloadedSongRef.current =
          null

        const nextAudio =
          nextAudioRef.current

        if (nextAudio) {
          nextAudio.pause()

          nextAudio.removeAttribute(
            "src",
          )

          nextAudio.load()
        }

        /* ===============================================
           CLOSE PREVIOUS INSIGHTS TRACK
        =============================================== */

        if (
          insightsTrackRef.current !==
          null
        ) {
          insightsPlayingRef.current =
            false

          stop()

          insightsTrackRef.current =
            null
        }

        /* ===============================================
           INVALIDATE RADIO
        =============================================== */

        radioPlayRequestId.current++

        /* ===============================================
           RADIO STATE
        =============================================== */

        setIsMusicLoading(
          true,
        )

        setIsPlaying(
          false,
        )

        musicHasStartedRef.current =
          false

        /* ===============================================
           STOP CURRENT AUDIO
        =============================================== */

        audio.pause()

        audio.onloadedmetadata =
          null

        audio.oncanplay =
          null

        audio.onplaying =
          null

        audio.onerror =
          null

        audio.onended =
          null

        audio.onwaiting =
          null

        audio.onstalled =
          null

        audio.onabort =
          null

        /* ===============================================
           OFFLINE
        =============================================== */

        const isOffline =
          songPlaylist?.id ===
            "offline" ||
          songPlaylist?.type ===
            "offline" ||
          (song as any)
            .offline === true ||
          (song as any)
            .type === "offline"

        let src:
          | string
          | null = null

        if (isOffline) {
          const blob =
            (song as any)
              .blob

          if (!blob) {
            setIsMusicLoading(
              false,
            )

            return
          }

          src =
            URL.createObjectURL(
              blob,
            )
        } else {
          src =
            song.link ||
            song.url ||
            null
        }

        if (!src) {
          setIsMusicLoading(
            false,
          )

          return
        }

        /* ===============================================
           LOAD AUDIO
        =============================================== */

        audio.removeAttribute(
          "src",
        )

        audio.load()

        audio.src =
          src

        audio.currentTime =
          0

        /* ===============================================
           PLAYLIST
        =============================================== */

        if (
          songPlaylist
        ) {
          setCurrentPlaylist(
            songPlaylist,
          )
        }

        const playlistId =
          songPlaylist?.id ??
          null

        /* ===============================================
           TRACK DATA
        =============================================== */

        updateTrackData({
          type: "song",

          track: src,

          trackName:
            song.songName,

          trackArtist:
            song.songArtist,

          trackImg:
            song.songimg,

          playlistLink:
            songPlaylist?.link,

          playlistTitle:
            songPlaylist?.title,

          trackKey: [
            playlistId,
            index,
          ],

          trackTimeElapsed: 0,

          _ts: Date.now(),
        })

        /* ===============================================
           REACT STATE
        =============================================== */

        setCurrentSong(
          song,
        )

        setCurrentSongIndex(
          index,
        )

        /* ===============================================
           REQUEST
        =============================================== */

        const requestId =
          ++playRequestId.current

        musicHasStartedRef.current =
          false



        /* ===============================================
           EVENTS
        =============================================== */

        attachMusicAudioEvents(
          audio,
          song,
          songPlaylist ?? {},
          index,
          src,
          requestId,
        )

        /* ===============================================
           START LOADING
        =============================================== */

        audio.load()

        audio.play().catch(
          (error: any) => {
            if (
              requestId !==
              playRequestId.current
            ) {
              return
            }

            musicHasStartedRef.current =
              false

            setIsMusicLoading(
              false,
            )

            setIsPlaying(
              false,
            )

            if (
              error?.name !==
              "AbortError"
            ) {
              console.error(
                "[MusicPlayer] audio.play error:",
                error,
              )
            }
          },
        )
      },
      [
        stop,
        updateTrackData,
        attachMusicAudioEvents,
        setCurrentPlaylist,
        setCurrentSong,
        setCurrentSongIndex,
      ],
    )

  /* =======================================================
     PRELOAD NEXT SONG
  ======================================================= */

  const preloadNextSong =
    useCallback(
      () => {
        const songPlaylist =
          currentPlaylist

        if (
          !songPlaylist
        ) {
          return
        }

        const list =
          getPlaylistData(
            songPlaylist,
          )

        if (
          !list.length
        ) {
          return
        }

        let nextIndex: number

        if (
          isShuffle &&
          list.length > 1
        ) {
          do {
            nextIndex =
              Math.floor(
                Math.random() *
                  list.length,
              )
          } while (
            nextIndex ===
            currentSongIndex
          )
        } else {
          nextIndex =
            (currentSongIndex +
              1) %
            list.length
        }

        const nextSong =
          list[nextIndex]

        if (!nextSong) {
          return
        }

        const src =
          nextSong.link ||
          nextSong.url

        if (!src) {
          return
        }

        const nextAudio =
          nextAudioRef.current

        if (!nextAudio) {
          return
        }

        /* ===============================================
           ALREADY PRELOADED
        =============================================== */

        if (
          preloadedSongRef.current
            ?.index ===
            nextIndex &&
          preloadedSongRef.current
            ?.src ===
            src
        ) {
          return
        }

        const requestId =
          ++preloadRequestId.current

        /* ===============================================
           RESET NEXT AUDIO
        =============================================== */

        nextAudio.pause()

        nextAudio.removeAttribute(
          "src",
        )

        nextAudio.load()

        nextAudio.src =
          src

        nextAudio.preload =
          "auto"

        /* ===============================================
           STORE
        =============================================== */

        preloadedSongRef.current =
          {
            song: nextSong,

            index:
              nextIndex,

            playlist:
              songPlaylist,

            src,
          }

        /* ===============================================
           LOAD
        =============================================== */

        nextAudio.load()

        nextAudio.addEventListener(
          "canplay",
          () => {
            if (
              requestId !==
              preloadRequestId.current
            ) {
              return
            }

            console.log(
              "[Music] NEXT TRACK READY:",
              nextSong.songName,
            )
          },
          {
            once: true,
          },
        )
      },
      [
        currentPlaylist,
        currentSongIndex,
        isShuffle,
      ],
    )

  /* =======================================================
     PRELOAD AFTER CURRENT TRACK
  ======================================================= */

  useEffect(() => {
    if (
      !currentSong ||
      !currentPlaylist
    ) {
      return
    }

    const timer =
      window.setTimeout(
        () => {
          preloadNextSong()
        },
        500,
      )

    return () => {
      window.clearTimeout(
        timer,
      )
    }
  }, [
    currentSong,
    currentPlaylist,
    currentSongIndex,
    isShuffle,
    preloadNextSong,
  ])
const resumeAudio =
  useCallback(
    async () => {
      const audio =
        audioRef.current

      if (!audio) {
        setIsMusicLoading(false)
        setIsPlaying(false)

        return false
      }

      /* ===============================================
         NO AUDIO SOURCE
      =============================================== */

      if (!audio.src) {
        wantsToPlayRef.current =
          false

        setIsMusicLoading(false)
        setIsPlaying(false)

        return false
      }

      /* ===============================================
         USER INTENTION
      =============================================== */

      wantsToPlayRef.current =
        true

      setIsMusicLoading(true)
      setIsPlaying(false)

      try {
        await audio.play()

        /*
         * NO marcamos aquí playing=true.
         *
         * El evento "playing" lo confirma.
         */

        return true
      } catch (error: any) {
        if (
          error?.name ===
          "AbortError"
        ) {
          return false
        }

        wantsToPlayRef.current =
          false

        musicHasStartedRef.current =
          false

        setIsMusicLoading(false)
        setIsPlaying(false)

        console.error(
          "[MusicPlayer] Error reanudando:",
          error,
        )

        return false
      }
    },
    [],
  )

  /* =======================================================
     SAVE STATE
  ======================================================= */

  const saveState =
    useCallback(
      (time?: number) => {
        if (
          typeof window ===
            "undefined" ||
          !currentSong ||
          !currentPlaylist
        ) {
          return
        }

        const audio =
          audioRef.current

        const state = {
          playlistId:
            currentPlaylist.title,

          songIndex:
            currentSongIndex,

          time:
            time ??
            audio?.currentTime ??
            0,

          trackData: {
            track:
              currentSong.link ||
              currentSong.url,

            trackName:
              currentSong.songName,

            trackArtist:
              currentSong.songArtist,

            trackImg:
              currentSong.songimg,

            playlistLink:
              currentPlaylist.link,

            playlistTitle:
              currentPlaylist.title,

            type:
              currentPlaylist.type,

            trackKey: [
              currentPlaylist.id,
              currentSongIndex,
            ],
          },
        }

        try {
          localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(
              state,
            ),
          )
        } catch (error) {
          console.warn(
            "[Music] No se pudo guardar el estado:",
            error,
          )
        }
      },
      [
        currentSong,
        currentPlaylist,
        currentSongIndex,
      ],
    )

  /* =======================================================
     RESTORE LAST SESSION
  ======================================================= */

  useEffect(() => {
    if (
      typeof window ===
      "undefined"
    ) {
      return
    }

    const saved =
      localStorage.getItem(
        STORAGE_KEY,
      )

    /*
     * Primera apertura:
     * no hay reproducción anterior.
     */

    if (!saved) {
      return
    }

    try {
      const parsed =
        JSON.parse(saved)

      /* ===============================================
         VALIDATION
      =============================================== */

      if (
        typeof parsed.songIndex !==
          "number" ||
        !parsed.playlistId
      ) {
        return
      }

      /* ===============================================
         FIND PLAYLIST
      =============================================== */

      const foundPlaylist =
        playlist.find(
          (p) =>
            p.title ===
            parsed.playlistId,
        )

      if (!foundPlaylist) {
        console.warn(
          "[Music] La playlist guardada ya no existe.",
        )

        return
      }

      const list =
        getPlaylistData(
          foundPlaylist,
        )

      if (
        !list.length
      ) {
        return
      }

      const song =
        list[
          parsed.songIndex
        ]

      if (!song) {
        console.warn(
          "[Music] La canción guardada ya no existe.",
        )

        return
      }

      const src =
        song.link ||
        song.url

      if (!src) {
        return
      }

      /* ===============================================
         RESTORE REACT STATE
      =============================================== */

      setCurrentPlaylist(
        foundPlaylist,
      )

      setCurrentSong(
        song,
      )

      setCurrentSongIndex(
        parsed.songIndex,
      )

      updateTrackData(
        parsed.trackData ??
          {
            type: "song",

            track: src,

            trackName:
              song.songName,

            trackArtist:
              song.songArtist,

            trackImg:
              song.songimg,

            playlistLink:
              foundPlaylist.link,

            playlistTitle:
              foundPlaylist.title,

            trackKey: [
              foundPlaylist.id,
              parsed.songIndex,
            ],

            trackTimeElapsed:
              typeof parsed.time ===
              "number"
                ? parsed.time
                : 0,

            _ts: Date.now(),
          },
      )

      /* ===============================================
         RESTORE AUDIO
      =============================================== */

      const audio =
        audioRef.current

      if (!audio) {
        return
      }

      audio.pause()

      wantsToPlayRef.current =
        false

      musicHasStartedRef.current =
        false

      setIsMusicLoading(
        false,
      )

      setIsPlaying(
        false,
      )

      audio.src =
        src

      const requestId =
        ++playRequestId.current

      attachMusicAudioEvents(
        audio,
        song,
        foundPlaylist,
        parsed.songIndex,
        src,
        requestId,
      )

      audio.load()

      /* ===============================================
         RESTORE TIME
      =============================================== */

      const restoreTime =
        () => {
          const savedTime =
            typeof parsed.time ===
            "number"
              ? parsed.time
              : 0

          if (
            savedTime > 0 &&
            Number.isFinite(
              savedTime,
            )
          ) {
            try {
              audio.currentTime =
                savedTime
            } catch {}
          }
        }

      audio.addEventListener(
        "loadedmetadata",
        restoreTime,
        {
          once: true,
        },
      )

      audio.addEventListener(
        "canplay",
        restoreTime,
        {
          once: true,
        },
      )

      if (
        audio.readyState >=
        1
      ) {
        restoreTime()
      }

      /*
       * IMPORTANTE:
       *
       * Restauramos la canción,
       * pero NO reproducimos.
       */

      setIsPlaying(
        false,
      )
    } catch (error) {
      console.error(
        "[Music] Error restaurando sesión:",
        error,
      )

      localStorage.removeItem(
        STORAGE_KEY,
      )
    }

    /*
     * Se ejecuta una sola vez.
     *
     * La playlist base no debería
     * cambiar durante la vida
     * del reproductor.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* =======================================================
     SAVE BEFORE EXIT
  ======================================================= */

  useEffect(() => {
    const saveBeforeExit =
      () => {
        const audio =
          audioRef.current

        if (!audio) {
          return
        }

        saveState(
          audio.currentTime,
        )
      }

    const handleVisibilityChange =
      () => {
        if (
          document.visibilityState ===
          "hidden"
        ) {
          saveBeforeExit()
        }
      }

    window.addEventListener(
      "beforeunload",
      saveBeforeExit,
    )

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    )

    return () => {
      window.removeEventListener(
        "beforeunload",
        saveBeforeExit,
      )

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      )
    }
  }, [
    currentSong,
    currentPlaylist,
    currentSongIndex,
    saveState,
  ])

  /* =======================================================
     AUDIO EVENTS
  ======================================================= */

  useEffect(() => {
    const audio =
      audioRef.current

    if (!audio) {
      return
    }

    /* =====================================================
       TIME UPDATE
    ===================================================== */
    const onTime =
      () => {
        /* ===============================================
           TRACK POSITION
        =============================================== */

        if (
          currentTrackDataRef.current
        ) {
          updateTrackData({
            ...currentTrackDataRef.current,

            trackTimeElapsed:
              audio.currentTime,
          })
        }

        /* ===============================================
           SAVE POSITION
        =============================================== */

        if (
          currentSong &&
          currentPlaylist
        ) {
          saveState(
            audio.currentTime,
          )
        }
      }

    /* =====================================================
       PLAY
    ===================================================== */
    const onPlay =
      () => {
      }

    /* =====================================================
       PAUSE
    ===================================================== */
    const onPause =
      () => {
        if (
          audioRef.current !==
          audio
        ) {
          return
        }

        setIsPlaying(
          false,
        )

        if (
          currentTrackDataRef
            .current
            ?.type !==
          "radio"
        ) {
          setIsMusicLoading(
            false,
          )

          /* setMediaPlaybackState(
            "paused",
          ) */

          if (
            insightsPlayingRef.current
          ) {
            insightsPlayingRef.current =
              false

            pause()
          }
        }
      }

    /* =====================================================
       ENDED
    ===================================================== */

    const onEnded =
      () => {
        const track =
          currentTrackDataRef
            .current

        /* ===============================================
           RADIO
        =============================================== */

        if (
          track?.type ===
          "radio"
        ) {
          setIsPlaying(
            false,
          )

          setIsMusicLoading(
            false,
          )

          return
        }

        /* ===============================================
           INSIGHTS
        =============================================== */

        insightsPlayingRef.current =
          false

        stop()

        insightsTrackRef.current =
          null

        /* ===============================================
           PRELOADED NEXT
        =============================================== */

        /* const playedPreloaded =
          await playPreloadedNext()

        if (
          playedPreloaded
        ) {
          return
        } */

        /* ===============================================
           FALLBACK
        =============================================== */

        if (
          !currentPlaylist
        ) {
          return
        }

        const list =
          getPlaylistData(
            currentPlaylist,
          )

        if (
          !list.length
        ) {
          return
        }

        let nextIndex: number

        if (
          isShuffle &&
          list.length > 1
        ) {
          do {
            nextIndex =
              Math.floor(
                Math.random() *
                  list.length,
              )
          } while (
            nextIndex ===
            currentSongIndex
          )
        } else {
          nextIndex =
            (currentSongIndex +
              1) %
            list.length
        }

        playSong(
          list[nextIndex],
          nextIndex,
          currentPlaylist,
        )
      }

    /* =====================================================
       LISTENERS
    ===================================================== */

    audio.addEventListener(
      "timeupdate",
      onTime,
    )

    audio.addEventListener(
      "ended",
      onEnded,
    )

    audio.addEventListener(
      "play",
      onPlay,
    )

    audio.addEventListener(
      "pause",
      onPause,
    )

    return () => {
      audio.removeEventListener(
        "timeupdate",
        onTime,
      )

      audio.removeEventListener(
        "ended",
        onEnded,
      )

      audio.removeEventListener(
        "play",
        onPlay,
      )

      audio.removeEventListener(
        "pause",
        onPause,
      )
    }
  }, [
    currentSong,
    currentPlaylist,
    currentSongIndex,
    isShuffle,
    playSong,
    updateTrackData,
    currentTrackDataRef,
    saveState,
    pause,
    stop,
  ])



  /* =======================================================
     SEEK
  ======================================================= */

  const seek =
    useCallback(
      (time: number) => {
        const audio =
          audioRef.current

        if (!audio) {
          return
        }

        if (
          !Number.isFinite(
            time,
          )
        ) {
          return
        }

        const duration =
          audio.duration

        const nextTime =
          Number.isFinite(
            duration,
          ) &&
          duration > 0
            ? Math.min(
                Math.max(
                  time,
                  0,
                ),
                duration,
              )
            : Math.max(
                time,
                0,
              )

        audio.currentTime =
          nextTime

        if (
          currentTrackDataRef.current
        ) {
          updateTrackData({
            ...currentTrackDataRef.current,

            trackTimeElapsed:
              nextTime,
          })
        }

        saveState(
          nextTime,
        )
      },
      [
        updateTrackData,
        saveState,
      ],
    )

  /* =======================================================
     RETURN
  ======================================================= */
  return {
    audioRef,
    nextAudioRef,
    isPlaying,
    setIsPlaying,
    isLoading,
    isMusicLoading,
    setIsMusicLoading,
    isBuffering,
    setIsBuffering,
    playSong,
    resumeAudio,
    seek,
    insightsPlayingRef,
    insightsTrackRef,
    radioPlayRequestId,
    radioHasStartedRef,
    wantsToPlayRef,
    startTrack,
    pause,

    resume,
    stop,
  }
}
